import { useRouter } from "next/router";

import { Currency } from "@/Apollo/schema";
import { localeCurrency } from "@/features/display-currency/currency-url";
import { usePageDisplayCurrency } from "@/features/display-currency/PageDisplayCurrency";
import {
  DISPLAY_CURRENCY_GATE,
  displayCurrencyForced,
} from "@/features/display-currency/display-currency.static";
import { useDisplayCurrencyStore } from "@/features/display-currency/display-currency.store";
import { toDisplayCurrency } from "@/features/display-currency/display-currency.utils";
import { useFeatureGate } from "@/features/statsig/gate.hooks";

/** The currency the page's locale sets (`LOCALE_CURRENCIES`, `/en-us` = USD, WHO-5766), or `undefined`. */
const useLocaleCurrency = (): Currency | undefined =>
  localeCurrency(useRouter().locale);

/**
 * Whether buyers can see and pick a display currency: the `web_display_currency` gate, off in
 * production until the Phase 1 launch (the apps' `DisplayCurrencyPolicy.isSelectionEnabled`),
 * or the preview override above.
 */
export const useDisplayCurrencyEnabled = (): boolean => {
  const { isEnabled } = useFeatureGate(DISPLAY_CURRENCY_GATE);

  return displayCurrencyForced() || isEnabled;
};

/**
 * The buyer's display currency, for the queries to ask for (WHO-5142, WHO-5143): their pick, the
 * account's (taken over by `DisplayCurrencySync`), or the first guess from their country.
 * `undefined` means each listing's own currency: the gate is off, or EUR.
 *
 * Until the store has read the cookie (it does so after mount, once the gate is on), it is the
 * currency the server rendered the page in, so the first render matches the server's (WHO-6199):
 * a `?currency=` product or search page's, each listing's own on every other page.
 *
 * A locale whose path sets the currency wins over all of that (`LOCALE_CURRENCIES`, WHO-5766):
 * every `/en-us` page shows USD, the gate and the visitor's pick notwithstanding.
 */
export const useDisplayCurrency = (): Currency | undefined => {
  const fromLocale = useLocaleCurrency();
  const enabled = useDisplayCurrencyEnabled();
  const pageCurrency = usePageDisplayCurrency();
  const picked = useDisplayCurrencyStore(state => state.picked);
  const guess = useDisplayCurrencyStore(state => state.guess);
  const hydrated = useDisplayCurrencyStore(state => state.hydrated);

  if (fromLocale) {
    return fromLocale;
  }

  if (!hydrated) {
    return pageCurrency;
  }

  return enabled ? toDisplayCurrency(picked ?? guess) : undefined;
};

/**
 * The currency the picker shows as chosen: EUR included, `null` before it is known. On a locale
 * whose path sets the currency (`/en-us`) that one, since it is what the page shows (WHO-5766).
 */
export const usePickedDisplayCurrency = (): Currency | null => {
  const fromLocale = useLocaleCurrency();
  const picked = useDisplayCurrencyStore(state => state.picked);
  const guess = useDisplayCurrencyStore(state => state.guess);
  const hydrated = useDisplayCurrencyStore(state => state.hydrated);

  if (fromLocale) {
    return fromLocale;
  }

  return hydrated ? picked ?? guess : null;
};
